import { createSelector } from "@reduxjs/toolkit";
import { getOfferSelectionLineIds } from "../../../utils/cart/loyaltyOfferResponse";
import { orderLinesToDisplayItems } from "../../../utils/cart/orderLineDisplay";
import { findLegacyRedeemedOffer } from "../../../utils/help/offers/findLegacyRedeemedOffer";
import type { RootState } from "../app/store";
import { CartItemType } from "../types/CartLoyaltyV2Types";
import { LoyaltyOfferStatus } from "../types/OrderCartV2Types";
import {
  selectLoyaltyAddedItemLineIds,
  selectLoyaltyCartLines,
  selectLoyaltyCartResult,
  selectLoyaltyRedemption,
  selectRedeemedOfferApplicationId,
} from "./loyaltyCartSelectors";
import { selectRedeemedLoyaltyOffer } from "./loyaltyOfferSelectors";
import { selectUseLoyaltyService } from "./loyaltyServiceSelectors";
import { selectMenuLookups } from "./menuLookupSelectors";

const selectCartDisplayItems: (state: RootState) => CartItemType[] =
  createSelector(
    [
      selectUseLoyaltyService,
      selectLoyaltyCartLines,
      selectLoyaltyCartResult,
      selectMenuLookups,
      selectLoyaltyRedemption,
      (state: RootState) => state.cartLoyaltyV2Reducer.cart.items,
    ],
    (
      useLoyaltyService,
      loyaltyCartLines,
      loyaltyCartResult,
      menuLookups,
      loyaltyRedemption,
      legacyItems
    ) => {
      if (!useLoyaltyService) {
        return legacyItems as CartItemType[];
      }

      const offerLineIds = getOfferSelectionLineIds({
        redemption: loyaltyRedemption,
        result: loyaltyCartResult,
      });
      const offerApplicationId =
        loyaltyCartResult?.appliedOffer?.offerApplicationId ??
        loyaltyRedemption?.offerApplicationId;

      return orderLinesToDisplayItems(
        loyaltyCartLines,
        menuLookups,
        loyaltyCartResult?.lines,
        offerLineIds,
        offerApplicationId
      );
    }
  );

export const selectCartItems = (state: RootState) =>
  selectCartDisplayItems(state);

/**
 * Cart rows belonging to the active redemption — both sides of a guest-built
 * offer, since only the reward lines carry `appliedOffer` in the response.
 */
export const selectAppliedOfferCartItems = createSelector(
  selectCartDisplayItems,
  selectLoyaltyAddedItemLineIds,
  (items, addedLineIds) => {
    if (!addedLineIds?.length) {
      return [];
    }

    const added = new Set(addedLineIds);

    return items.filter((item) => item.lineId && added.has(item.lineId));
  }
);

/**
 * The applied offer's cart rows split by which side they were added for, so
 * editing it can rebuild the draft the guest assembled.
 */
export const selectCommittedOfferSides = createSelector(
  selectAppliedOfferCartItems,
  selectLoyaltyRedemption,
  (items, redemption) => {
    const buyIds = new Set(
      (redemption?.pendingTriggers ?? []).map((line) => line.lineId)
    );
    const rewardIds = new Set(
      (redemption?.pendingSelections ?? []).map((line) => line.lineId)
    );

    return {
      buy: items.filter((item) => item.lineId && buyIds.has(item.lineId)),
      reward: items.filter((item) => item.lineId && rewardIds.has(item.lineId)),
    };
  }
);

export const selectCartSubtotal = (state: RootState) =>
  selectUseLoyaltyService(state)
    ? selectLoyaltyCartResult(state)?.pricing?.subtotal ?? 0
    : state.cartLoyaltyV2Reducer.cart.subTotalPrice;

export const selectCartTotal = (state: RootState) =>
  selectUseLoyaltyService(state)
    ? selectLoyaltyCartResult(state)?.pricing?.total ?? 0
    : state.cartLoyaltyV2Reducer.cart.totalPrice;

export const selectCartDiscount = (state: RootState) =>
  selectUseLoyaltyService(state)
    ? selectLoyaltyCartResult(state)?.pricing?.discounts?.total ?? 0
    : state.cartLoyaltyV2Reducer.cart.discountedValue;

export const selectCartTax = (state: RootState) =>
  selectUseLoyaltyService(state)
    ? selectLoyaltyCartResult(state)?.pricing?.tax ?? 0
    : state.cartLoyaltyV2Reducer.cart.vat;

export const selectCartService = (state: RootState) =>
  selectUseLoyaltyService(state)
    ? selectLoyaltyCartResult(state)?.pricing?.serviceCharge ?? 0
    : state.cartLoyaltyV2Reducer.cart.service;

export const selectCartDeliveryFees = (state: RootState) =>
  selectUseLoyaltyService(state)
    ? selectLoyaltyCartResult(state)?.pricing?.delivery?.total ?? 0
    : state.cartLoyaltyV2Reducer.cart.deliveryFees;

export const selectCartPoints = (state: RootState) =>
  selectUseLoyaltyService(state)
    ? selectLoyaltyCartResult(state)?.points
    : undefined;

export const selectIsOfferApplied = (state: RootState) =>
  selectUseLoyaltyService(state)
    ? selectLoyaltyCartResult(state)?.appliedOffer?.status === "APPLIED"
    : !!state.cartLoyaltyV2Reducer.cart.isOfferApplied;

export const selectIsEligibleOffer = (state: RootState) =>
  selectUseLoyaltyService(state)
    ? selectIsOfferApplied(state)
    : state.cartLoyaltyV2Reducer.cart.isEligibleOffer;

export const selectAppliedOfferResponse = (state: RootState) =>
  selectUseLoyaltyService(state)
    ? selectLoyaltyCartResult(state)?.appliedOffer
    : undefined;

export const selectRemainingToEligible = (state: RootState) => {
  if (selectUseLoyaltyService(state)) {
    if (selectIsOfferApplied(state)) {
      return 0;
    }

    const requiredAdditional =
      selectLoyaltyCartResult(state)?.appliedOffer?.reason?.details
        ?.requiredAdditional;

    if (requiredAdditional !== undefined) {
      return Math.max(0, requiredAdditional);
    }

    const minSpend =
      selectLoyaltyRedemption(state)?.offer?.validity?.minimumSpendAmount ?? 0;
    const subtotal = selectCartSubtotal(state);

    return Math.max(0, minSpend - subtotal);
  }

  return state.cartLoyaltyV2Reducer.cart.remainingToEligiable;
};

export const selectCartItemsNumber = (state: RootState) => {
  if (selectUseLoyaltyService(state)) {
    return selectLoyaltyCartLines(state).reduce(
      (sum, line) => sum + line.quantity,
      0
    );
  }

  return state.cartLoyaltyV2Reducer.cart.cartItemsNumber;
};

export const selectOfferAppliedLineIds = createSelector(
  [selectLoyaltyCartResult],
  (loyaltyCartResult) =>
    (loyaltyCartResult?.lines ?? [])
      .filter((line) => line.appliedOffer)
      .map((line) => line.lineId)
);

/** Client-side offer line selections before/at calculate (addedLineIds / pendingSelections). */
export const selectHasPendingOfferSelections = (state: RootState) => {
  const redemption = selectLoyaltyRedemption(state);
  if (!redemption) {
    return false;
  }

  return (
    (redemption.pendingSelections?.length ?? 0) > 0 ||
    (redemption.addedLineIds?.length ?? 0) > 0
  );
};

export const selectIsRedeemedOfferServerApplied = (
  state: RootState,
  offer?: { id: string }
): boolean => {
  if (!selectUseLoyaltyService(state)) {
    return false;
  }

  const redeemed = offer ?? selectRedeemedLoyaltyOffer(state);
  if (!redeemed) {
    return false;
  }

  const loyaltyCartResult = selectLoyaltyCartResult(state);
  const redeemedOfferApplicationId = selectRedeemedOfferApplicationId(state);

  if (
    (loyaltyCartResult?.appliedOffer?.status === LoyaltyOfferStatus.APPLIED ||
      loyaltyCartResult?.appliedOffer?.reason?.code ===
        "MINIMUM_SPEND_NOT_MET") &&
    loyaltyCartResult.appliedOffer.offerId === redeemed.id
  ) {
    return true;
  }

  if (!redeemedOfferApplicationId) {
    return false;
  }

  return (loyaltyCartResult?.lines ?? []).some(
    (line) =>
      line.appliedOffer?.offerApplicationId === redeemedOfferApplicationId
  );
};

export const selectActiveRedeemedOffer = createSelector(
  (state: RootState) => state,
  selectUseLoyaltyService,
  selectRedeemedLoyaltyOffer,
  (state, useLoyaltyService, redeemedLoyaltyOffer) => {
    if (useLoyaltyService) {
      return redeemedLoyaltyOffer;
    }

    const cart = state.cartLoyaltyV2Reducer.cart;

    return findLegacyRedeemedOffer(
      state.cartAndLoyaltyReducer.offersArray,
      cart.redeemedOffer
    );
  }
);
